import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { BellRing, Play, Check, CheckCheck, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";

const SEV_STYLE = {
  INFO: "bg-slate-100 text-slate-700", WARNING: "bg-amber-100 text-amber-800",
  CRITICAL: "bg-red-100 text-red-700", BLOCKED: "bg-black text-white",
};
const NUM = [
  ["low_stock_threshold", "Low-Stock Threshold"], ["critical_stock_threshold", "Critical-Stock Threshold"],
  ["pct_change_threshold", "% Change Threshold"], ["unit_change_threshold", "Unit Change Threshold"],
  ["freshness_limit_minutes", "Freshness Limit (min)"], ["alert_cooldown_minutes", "Alert Cooldown (min)"],
];
const BOOL = [
  ["suppress_repeated", "Suppress repeated alerts"], ["auto_pause_publishing", "Auto-pause publishing"],
  ["auto_set_zero", "Auto-set quantity to zero"], ["require_admin_approval_restock", "Require approval before restock"],
  ["escalation_email", "Escalation email"], ["resolution_email", "Resolution email"],
  ["simulation_mode", "Simulation mode"], ["activation_approved", "Activation approved"],
];

export default function AdminInventoryAlerts() {
  const [s, setS] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const loadSettings = () => adminClient().get("/admin/cj/alert-settings").then(({ data }) => setS(data)).catch(() => setS({}));
  const loadAlerts = () => adminClient().get("/admin/cj/alerts").then(({ data }) => setData(data)).catch(() => setData({ alerts: [], status_counts: {} }));
  useEffect(() => { loadSettings(); loadAlerts(); }, []);

  const setF = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const saveSettings = async () => {
    try {
      const p = { reason: "admin alert settings change" };
      NUM.forEach(([k]) => (p[k] = Number(s[k]) || 0));
      BOOL.forEach(([k]) => (p[k] = !!s[k]));
      p.notification_channels = s.notification_channels || ["in_app"];
      p.external_alert_severities = s.external_alert_severities || ["CRITICAL", "BLOCKED"];
      p.email_destination = s.email_destination || "";
      p.email_cooldown_hours = Number(s.email_cooldown_hours) || 6;
      const { data } = await adminClient().put("/admin/cj/alert-settings", p);
      setS(data); toast.success("Alert settings saved");
    } catch { toast.error("Save failed"); }
  };

  const scan = async () => {
    setBusy(true);
    try { const { data } = await adminClient().post("/admin/cj/alerts/scan?limit=5"); toast.success(`Scan done — ${data.shopify_writes} Shopify writes, ${data.alerts_raised.length} raised`); loadAlerts(); }
    catch { toast.error("Scan failed"); } finally { setBusy(false); }
  };
  const act = async (id, kind, note = "") => {
    try { await adminClient().post(`/admin/cj/alerts/${id}/${kind}`, { note }); toast.success(`Alert ${kind}`); loadAlerts(); }
    catch { toast.error("Action failed"); }
  };
  const recheck = async (pid) => {
    try { const { data } = await adminClient().post(`/admin/cj/alerts/recheck/${pid}`); toast.success(`Rechecked — ${data.evaluation.decision}`); loadAlerts(); }
    catch { toast.error("Recheck failed"); }
  };
  const [notif, setNotif] = useState(null);
  const runNotifTest = async () => {
    try { const { data } = await adminClient().post("/admin/cj/alerts/notification-test"); setNotif(data); toast.success(`Notification tests ${data.all_scenarios_passed ? "passed" : "failed"}`); }
    catch { toast.error("Notification test failed"); }
  };

  if (!s || !data) return <p className="text-sm text-muted-foreground">Loading alerts…</p>;
  const c = data.status_counts || {};

  return (
    <div data-testid="admin-inventory-alerts">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><BellRing className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Inventory-Risk Alerts</h1></div>
        <button onClick={scan} disabled={busy} className="bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50" data-testid="alerts-scan-btn"><Play className="w-3.5 h-3.5 inline mr-1" />{busy ? "Scanning…" : "Run Scan"}</button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">CJ-sourced products only. Monitoring is <strong>read-only</strong> — no Shopify writes. Open {c.open || 0} · Acknowledged {c.acknowledged || 0} · Resolved {c.resolved || 0}</p>

      <div className="border border-black/10 bg-white p-5 mb-6" data-testid="alert-settings">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Alert Settings</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {NUM.map(([k, label]) => (
            <label key={k} className="text-[11px] font-medium text-muted-foreground">{label}
              <input type="number" value={s[k] ?? 0} onChange={(e) => setF(k, e.target.value)} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm text-taven-black outline-none focus:border-taven-gold" data-testid={`alert-${k}`} /></label>
          ))}
          <label className="text-[11px] font-medium text-muted-foreground">Notification Channels (comma)
            <input value={(s.notification_channels || []).join(",")} onChange={(e) => setF("notification_channels", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm text-taven-black outline-none focus:border-taven-gold" data-testid="alert-notification_channels" /></label>
          <label className="text-[11px] font-medium text-muted-foreground">Email Destination
            <input value={s.email_destination || ""} onChange={(e) => setF("email_destination", e.target.value)} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm text-taven-black outline-none focus:border-taven-gold" data-testid="alert-email_destination" /></label>
          <label className="text-[11px] font-medium text-muted-foreground">External Severities (comma)
            <input value={(s.external_alert_severities || []).join(",")} onChange={(e) => setF("external_alert_severities", e.target.value.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean))} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm text-taven-black outline-none focus:border-taven-gold" data-testid="alert-external_alert_severities" /></label>
          <label className="text-[11px] font-medium text-muted-foreground">Email Cooldown (hours)
            <input type="number" value={s.email_cooldown_hours ?? 6} onChange={(e) => setF("email_cooldown_hours", e.target.value)} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm text-taven-black outline-none focus:border-taven-gold" data-testid="alert-email_cooldown_hours" /></label>
        </div>
        <div className="flex flex-wrap gap-4 mt-4">
          {BOOL.map(([k, label]) => (
            <label key={k} className="text-xs flex items-center gap-1.5 text-muted-foreground"><input type="checkbox" checked={!!s[k]} onChange={(e) => setF(k, e.target.checked)} data-testid={`alert-${k}`} /> {label}</label>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={saveSettings} className="bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="alert-save">Save Settings</button>
          <button onClick={runNotifTest} className="bg-taven-gold text-taven-black px-4 py-2 text-xs font-bold uppercase tracking-wider hover:brightness-95" data-testid="alert-notif-test">Test Notifications</button>
          <span className="text-[10px] text-muted-foreground">Email fires for CRITICAL/BLOCKED to {s.email_destination || "—"} · gated (simulated) until activation.</span>
        </div>
        {notif && (
          <div className="mt-3 text-xs border-t border-black/10 pt-3" data-testid="notif-test-result">
            <p className={`font-bold ${notif.all_scenarios_passed ? "text-green-700" : "text-red-600"}`}>Notification tests: {notif.all_scenarios_passed ? "ALL PASSED" : "FAILED"} · mode {notif.email_delivery_mode}</p>
            <ul className="mt-1 space-y-0.5">
              {notif.results.map((r) => (<li key={r.scenario}>• {r.scenario}: sent {r.emails_sent} / expected {r.expected} {r.emails_sent === r.expected ? "✓" : "✗"}</li>))}
            </ul>
            <p className="mt-1 text-muted-foreground">Preview: {notif.email_payload_preview.subject} → {notif.email_payload_preview.to} · masked ID: {String(notif.email_payload_preview.shopify_id_masked)} · Shopify writes: {notif.shopify_writes}</p>
          </div>
        )}
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto" data-testid="alerts-table">
        <table className="w-full text-xs">
          <thead className="bg-taven-gray/60 text-left"><tr>
            <th className="px-2 py-2">Product</th><th className="px-2 py-2">CJ PID</th><th className="px-2 py-2">CJ Qty</th>
            <th className="px-2 py-2">Buffer</th><th className="px-2 py-2">Shopify Qty</th><th className="px-2 py-2">WH</th>
            <th className="px-2 py-2">Type</th><th className="px-2 py-2">Severity</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-black/5">
            {data.alerts.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">No alerts — inventory healthy.</td></tr>}
            {data.alerts.map((a) => (
              <React.Fragment key={a.id}>
                <tr data-testid={`alert-row-${a.id}`}>
                  <td className="px-2 py-2 max-w-[180px] truncate">{a.product_title || a.supplier_sku}</td>
                  <td className="px-2 py-2 font-mono">{a.cj_pid}</td>
                  <td className="px-2 py-2">{a.cj_quantity ?? "null"}</td>
                  <td className="px-2 py-2">{a.safety_buffer}</td>
                  <td className="px-2 py-2">{a.shopify_quantity ?? "—"}</td>
                  <td className="px-2 py-2">{a.warehouse || "—"}</td>
                  <td className="px-2 py-2">{a.alert_type}</td>
                  <td className="px-2 py-2"><span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${SEV_STYLE[a.severity]}`}>{a.severity}</span></td>
                  <td className="px-2 py-2">{a.status}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <button title="Acknowledge" onClick={() => act(a.id, "acknowledge", "acknowledged from console")} className="text-amber-600 hover:text-amber-800" data-testid={`ack-${a.id}`}><Check className="w-4 h-4" /></button>
                      <button title="Resolve" onClick={() => act(a.id, "resolve", "resolved from console")} className="text-green-600 hover:text-green-800" data-testid={`resolve-${a.id}`}><CheckCheck className="w-4 h-4" /></button>
                      <button title="Recheck" onClick={() => recheck(a.cj_pid)} className="text-blue-600 hover:text-blue-800" data-testid={`recheck-${a.id}`}><RefreshCw className="w-4 h-4" /></button>
                      <button title="History" onClick={() => setOpen(open === a.id ? null : a.id)} className="text-muted-foreground" data-testid={`hist-${a.id}`}>{open === a.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
                    </div>
                  </td>
                </tr>
                {open === a.id && (
                  <tr className="bg-taven-gray/20"><td colSpan={10} className="px-3 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Audit History · SKU {a.supplier_sku} · first {String(a.first_detected).slice(0, 19)} · detected {a.detect_count}× · assigned {a.assigned_admin || "—"}</p>
                    <ul className="text-[11px] space-y-0.5">
                      {(a.history || []).map((h, i) => (<li key={i}>• <strong>{h.action}</strong> — {h.actor} @ {String(h.at).slice(0, 19)} {h.note ? `(${h.note})` : ""}</li>))}
                    </ul>
                    {a.resolution_notes && <p className="text-[11px] mt-1 text-green-700">Resolution: {a.resolution_notes}</p>}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
